import { useState } from 'react';

import classNames from 'classnames';
import { z } from 'zod';

import Editor from '@monaco-editor/react';

interface JsonFieldProps<T> {
  value: T;
  schema: z.ZodType<T>;
  onChange: (value: T) => void;
}

// Keeps its own text so a half-typed edit stays on screen; the draft only takes valid JSON of the
// right shape. Remount it (via key) to load a different value.
const JsonField = <T,>({ value, schema, onChange }: JsonFieldProps<T>) => {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [problem, setProblem] = useState<string>();

  const handleChange = (next = '') => {
    setText(next);

    let parsed: unknown;
    try {
      parsed = JSON.parse(next);
    } catch (error) {
      setProblem(`Invalid JSON: ${error instanceof Error ? error.message : String(error)}`);
      return;
    }

    const result = schema.safeParse(parsed);
    if (!result.success) {
      const [issue] = result.error.issues;
      setProblem(`${issue.path.join('.') || 'JSON'}: ${issue.message}`);
      return;
    }

    setProblem(undefined);
    onChange(result.data);
  };

  return (
    <div>
      <div
        className={classNames('border rounded-lg overflow-hidden', {
          'border-neutral-20': !problem,
          'border-danger-50': problem,
        })}
      >
        <Editor
          height="380px"
          language="json"
          value={text}
          onChange={handleChange}
          theme="vs-light"
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            automaticLayout: true,
            tabSize: 2,
            wordWrap: 'on',
            wrappingIndent: 'indent',
          }}
        />
      </div>
      {problem ? (
        <p className="mt-2 text-sm text-danger-60">{problem} — not applied until it’s fixed.</p>
      ) : (
        <p className="mt-2 text-sm text-success-60">
          ✓ Valid JSON · {text.split('\n').length} lines
        </p>
      )}
    </div>
  );
};

export default JsonField;
